import React, { useContext, useEffect, useState, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation, Trans } from 'react-i18next';
import { ShieldCheckIcon } from 'lucide-react';

import Card from 'components/Card';
import Spinner from 'components/Spinner';
import Pagination from 'components/Pagination';
import HistoryItem from 'components/HistoryItem';
import { actions, getSign } from 'components/HistoryItem';
import Button from 'components/Button';
import AccountSetUpButton from 'containers/AccountSetUpButton';

import { ZkAccountContext, PoolContext } from 'contexts';
import { useWindowDimensions } from 'hooks';
import { decodeTextFromData } from 'utils';

export default () => {
  const { t } = useTranslation();
  const {
    histories, zkAccount, pendingDirectDepositsByPool,
    isLoadingZkAccount, isLoadingHistory,
  } = useContext(ZkAccountContext);
  const { availablePools } = useContext(PoolContext);
  const { width } = useWindowDimensions();
  const isMobile = width <= 500;

  const pageSize = 5;
  const [currentPage, setCurrentPage] = useState(1);

  const isLoading = isLoadingZkAccount || isLoadingHistory;
  const title = t('history.title');

  const items = useMemo(() => {
    if (!histories) return [];

    const combined = [];

    availablePools.forEach(pool => {
      const poolAlias = pool.alias;
      const poolHistory = histories[poolAlias] || [];
      const pendingDeposits = (pendingDirectDepositsByPool && pendingDirectDepositsByPool[poolAlias]) || [];

      // Add poolAlias metadata to each transaction
      const poolTransactions = pendingDeposits.concat(poolHistory).map(tx => ({
        ...tx,
        poolAlias,
        pool,
      }));

      combined.push(...poolTransactions);
    });

    // Sort by timestamp (most recent first)
    return combined.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
  }, [histories, pendingDirectDepositsByPool, availablePools]);

  const isHistoryEmpty = items.length === 0;

  useEffect(() => {
    setCurrentPage(1);
  }, [zkAccount]);

  const decodeMemoForAction = (extraInfo, actionIndex, actions) => {
    if (!extraInfo || !Array.isArray(extraInfo) || extraInfo.length === 0) {
      return '';
    }

    const message = extraInfo[actionIndex];
    if (!message || !message.data) {
      return '';
    }

    const decodedText = decodeTextFromData(message.data);
    return decodedText || '';
  };

  const exportData = () => {
    const headers = ['amount', 'from', 'to', 'toYourself', 'commitment', 'memo', 'failed', 'fee', 'from', 'txHash', 'type', 'state', 'failureReason', 'timestamp'];
    let csvContent = items.map(item => {
      let result = item.actions.map((action, actionIndex) => {
        const memo = decodeMemoForAction(item.extraInfo, actionIndex, item.actions);
        const memoEscaped = memo ? `"${memo.replace(/"/g, '""')}"` : '';

        return [
          getSign(item) + action.amount.toString(),
          action.from,
          action.to,
          action.isLoopback.toString(),
          item.commitment,
          memoEscaped,
          item.failed.toString(),
          item.fee,
          item.from,
          item.txHash,
          actions[item.type].name,
          // eslint-disable-next-line eqeqeq
          item.state == 2 ? "finalized" : "pending",
          item.failureReason,
          item.timestamp
        ].join(',')
      }).join('\n');
      return result;
    }).join('\n');

    const blob = new Blob([headers.join(',') + '\n' + csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `history-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }
  return (
    <ContentContainer>
      <Card title={!isHistoryEmpty ? title : null} titleStyle={{ marginBottom: 22 }}>
        {((isLoading && isHistoryEmpty) || isHistoryEmpty || !zkAccount) && (
          <Title>{title}</Title>
        )}
        {(isLoading && isHistoryEmpty) && (
          <Spinner size={60} />
        )}
        {(!isLoading && isHistoryEmpty && zkAccount) && (
          <EmptyState>
            <EmptyStateIcon>
              <ShieldCheckIcon size={40} />
            </EmptyStateIcon>
            <EmptyStateTitle>{t('history.emptyTitle')}</EmptyStateTitle>
            <EmptyStateDesc>{t('history.emptyDesc')}</EmptyStateDesc>
          </EmptyState>
        )}
        {(!isLoading && !zkAccount) && (
          <EmptyState>
            <EmptyStateIcon>
              <ShieldCheckIcon size={40} />
            </EmptyStateIcon>
            <EmptyStateTitle>{t('history.emptyTitle')}</EmptyStateTitle>
            <EmptyStateDesc>{t('history.emptyDescNoAccount')}</EmptyStateDesc>
            <AccountSetUpButton />
          </EmptyState>
        )}
        {!isHistoryEmpty && (
          <>
            <ScrollableItems>
              {items.slice((currentPage - 1) * pageSize, currentPage * pageSize).map((item, index) =>
                <HistoryItem key={index} item={item} zkAccount={zkAccount} isMobile={isMobile} />
              )}
            </ScrollableItems>
            {items.length > pageSize && (
              <Pagination
                currentPage={currentPage}
                numberOfPages={Math.ceil(items.length / pageSize)}
                setCurrentPage={setCurrentPage}
              />
            )}
          </>
        )}
      </Card>
      {!isHistoryEmpty &&
        <ExportButtonContainer>
          <ExportButton onClick={exportData}>
            Export to CSV
          </ExportButton>
        </ExportButtonContainer>}
    </ContentContainer>
  );
};

const Title = styled.span`
  font-size: 16px;
  color: ${({ theme }) => theme.text.color.primary};
  font-weight: ${({ theme }) => theme.text.weight.bold};
  text-align: center;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 32px 16px;
  text-align: center;
  width: 100%;
  box-sizing: border-box;

  button {
    width: 100%;
  }
`;

const EmptyStateIcon = styled.div`
  color: ${({ theme }) => theme.button.primary.background.default};
  opacity: 0.4;
  margin-bottom: 4px;
`;

const EmptyStateTitle = styled.span`
  font-size: 16px;
  font-weight: ${({ theme }) => theme.text.weight.bold};
  color: ${({ theme }) => theme.text.color.primary};
`;

const EmptyStateDesc = styled.span`
  font-size: 14px;
  line-height: 22px;
  color: ${({ theme }) => theme.text.color.secondary};
  max-width: 340px;
`;

const ScrollableItems = styled.div`
  overflow-y: auto;
  height: 440px;
  width: 100%;
`;

const ExportButton = styled(Button)`
  width: 100%;
  padding: 10px 20px;
`;

const ExportButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 5px 16px 0;
  margin-bottom: 25px;
  align-self: stretch;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: ${props => props.theme.color.white};
  border-radius: 8px;
  border: 2px solid ${props => props.theme.border.card};
  padding-bottom: 16px;

  @media only screen and (max-width: 560px) {
    margin: 15px 0;
  }
`;
